import { Button, TextField, type DialogProps } from '@mui/material';
import { useFormik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { object, string } from 'yup';
import { useCreateRole } from '~/api/roles';
import { Dialog, DialogForm, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib';
import { useAppContext } from '~/contexts';
import { useLink } from '~/hooks/link';

export const NewRoleDialog = (props: DialogProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const navigate = useNavigate();
  const link = useLink();

  const validationSchema = useMemo(
    () =>
      object({
        description: string().trim().label('Description'),
        name: string().required().trim().label('Name'),
      }),
    [],
  );

  const [createRole] = useCreateRole();

  // Sucks
  const close = () => props.onClose?.({}, 'backdropClick');

  const formik = useFormik({
    initialValues: { description: '', name: '' },
    onSubmit: async (values) => {
      const { data, errors } = await createRole({
        variables: {
          input: {
            ...validationSchema.cast(values),
            networkId: currentNetwork.id,
          },
        },
      });
      if (!errors?.length) {
        close();
        navigate(link(`/roles/${data?.createRole?.role.id}`));
      }
    },
    validationSchema,
  });

  return (
    <Dialog {...props}>
      <DialogTitle onClose={close}>{t('pages.roles.dialogs.new_role.title')}</DialogTitle>

      <DialogContent>
        <DialogForm id="new-role" onSubmit={formik.handleSubmit}>
          <label>
            <span className={`label required`}>{t('common.labels.name')}</span>
            <TextField
              autoFocus
              error={formik.touched.name && !!formik.errors.name}
              fullWidth
              helperText={
                (formik.touched.name && formik.errors.name
                  ? t('errors.validation.required', 'This field is required')
                  : '') || ' '
              }
              name="name"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.name}
            />
          </label>

          <label>
            <span className={`label`}>{t('common.labels.description')}</span>
            <TextField
              error={formik.touched.description && !!formik.errors.description}
              fullWidth
              helperText={(formik.touched.description && formik.errors.description) || ' '}
              minRows={4}
              maxRows={4}
              multiline
              name="description"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.description}
            />
          </label>
        </DialogForm>
      </DialogContent>

      <DialogActions>
        <Button disabled={formik.isSubmitting} onClick={close} variant="outlined">
          {t('common.buttons.cancel')}
        </Button>

        <Button
          color="primary"
          disabled={formik.isSubmitting || !formik.isValid}
          form="new-role"
          type="submit"
          variant="contained"
        >
          {t('common.buttons.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
